import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useBillingOrders, useCancelOrder, useRenewOrder, type BillingOrder } from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { i18n } from '@/lib/i18n'
import { isAddonOrder, isAiCreditOrder, resolvePlan, termLabel } from './plans'
import { goToPayment, methodShortLabel, orderActions, readReturnFrom, resultPath, resumePayment, writePendingOrder } from './payment-methods'
import { remainingText } from './bank-transfer-panel'

/** Được thao tác đơn này: đơn lượt AI theo quyền `ai_credits.create`; gói / mua thêm chỉ chủ sở hữu / quản trị (khớp backend canPayKind). */
const useCanPayOrder = (order: BillingOrder) => {
  const role = useAuthStore((s) => s.user?.role)
  const canBuyAi = useCanBuyAiCredits()
  return isAiCreditOrder(order) ? canBuyAi : role === 'owner' || role === 'admin'
}

/** Tên ngắn của đơn để khách nhận ra: "Gói Chuyên nghiệp · 360 ngày", "Mua thêm …", "Credit AI". */
export function orderTitleText(o: BillingOrder): string {
  if (isAiCreditOrder(o)) return i18n.t('billing:orderActions.buyAiTurns')
  const name = o.quote?.planName ?? resolvePlan(o.planId).name
  return isAddonOrder(o) ? i18n.t('billing:orderActions.buyMoreOfPlan', { plan: name }) : i18n.t('billing:orderActions.planWithTerm', { plan: name, term: termLabel(o.days) })
}

/** Tạo lại đơn (mã mới) rồi đưa khách sang thanh toán — dùng chung cho khung QR hết hạn và lịch sử. */
export function useRenewAndPay() {
  const renew = useRenewOrder()
  const run = (orderId: string) =>
    renew.mutate(
      { orderId, returnTo: resultPath(readReturnFrom()) },
      {
        onSuccess: (r) => {
          writePendingOrder(r.orderId)
          try {
            goToPayment(r)
          } catch (err) {
            writePendingOrder(null)
            toast.error(err instanceof Error ? err.message : i18n.t('billing:orderActions.cannotOpenCheckout'))
          }
        },
        onError: (err) => toast.error(apiError(err)),
      },
    )
  return { run, pending: renew.isPending }
}

/** Nút thao tác cho một đơn chưa trả: Tiếp tục thanh toán · Huỷ đơn · Tạo lại. Không hiện nếu không có thao tác hợp lệ. */
export function OrderActions({ order, size = 'sm' }: { order: BillingOrder; size?: 'sm' | 'default' }) {
  const { t } = useTranslation('billing')
  const canPay = useCanPayOrder(order)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(t)
  }, [])
  const { actions } = orderActions(order, now)
  const cancel = useCancelOrder()
  const renew = useRenewAndPay()
  const [confirming, setConfirming] = useState(false)
  if (!canPay || actions.length === 0) return null

  const resume = () => {
    try {
      resumePayment(order)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('orderActions.cannotOpenCheckout'))
    }
  }
  const doCancel = () =>
    cancel.mutate(order.id, {
      onSuccess: (o) => {
        setConfirming(false)
        if (o.status === 'paid') toast.success(t('orderActions.toastPaidRecorded'))
        else toast.success(t('orderActions.toastCancelled'))
      },
      onError: (err) => toast.error(apiError(err)),
    })

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {actions.includes('resume') && <Button size={size} onClick={resume}>{t('orderActions.continuePayment')}</Button>}
        {actions.includes('renew') && (
          <Button size={size} variant="outline" onClick={() => renew.run(order.id)} disabled={renew.pending}>
            {renew.pending && <Loader2 className="animate-spin" />} {t('orderActions.newCode')}
          </Button>
        )}
        {actions.includes('cancel') && <Button size={size} variant="ghost" onClick={() => setConfirming(true)}>{t('orderActions.cancelOrder')}</Button>}
      </div>
      <Dialog open={confirming} onOpenChange={(o) => !cancel.isPending && setConfirming(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('orderActions.cancelConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {orderTitleText(order)} · {formatVnd(order.amount)}. {t('orderActions.cancelConfirmDesc')}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('orderActions.cancelConfirmNote')}
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={cancel.isPending}>{t('orderActions.keepOrder')}</Button>
            <Button variant="destructive" onClick={doCancel} disabled={cancel.isPending}>
              {cancel.isPending && <Loader2 className="animate-spin" />} {t('orderActions.cancelOrder')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Khung "Bạn có đơn đang chờ thanh toán" — hiện ở các trang Gói dịch vụ / Thay đổi gói / Mua thêm khi không có khung QR đang mở.
 * Giúp khách quay lại đơn cũ (đóng QR, mở tab khác…) thay vì đặt thêm đơn; cũng là chỗ huỷ khi mã ưu đãi đang bị đơn chờ giữ.
 */
export function PendingOrderBanner() {
  const { t } = useTranslation('billing')
  const role = useAuthStore((s) => s.user?.role)
  const canPay = role === 'owner' || role === 'admin'
  const canBuyAi = useCanBuyAiCredits()
  const { data } = useBillingOrders()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  // Người chỉ có quyền mua lượt AI chỉ được nhắc đơn lượt AI (đơn gói / mua thêm vẫn chỉ chủ sở hữu / quản trị).
  const order = data?.orders.find((o) => (canPay || (canBuyAi && isAiCreditOrder(o))) && orderActions(o, now).actions.includes('resume'))
  if (!order) return null
  const left = remainingText(order.expiresAt, now)
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="font-medium">{t('orderActions.bannerTitle')}</p>
        <p className="text-muted-foreground">
          {orderTitleText(order)} · {formatVnd(order.amount)} · {methodShortLabel(order.provider)}
          {left && <> · {t('pendingBar.leftPrefix')} <span className="font-mono tabular-nums">{left}</span></>}
        </p>
        {order.quote?.promo && (
          <p className="text-xs text-muted-foreground">{t('orderActions.bannerPromoHeld', { code: order.quote.promo.code })}</p>
        )}
      </div>
      <OrderActions order={order} size="default" />
    </div>
  )
}
